<template>
    <div class="container-fluid pb-5">
        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Umowy zadatku</h3>
                <p class="text-muted small mb-0">Lista umów zadatku wystawionych na koncie</p>
            </div>
            <button
                class="btn btn-primary-dark d-flex align-items-center justify-content-center gap-2 w-100 btn-responsive-auto"
                @click="openModalForCreate()">
                <i class="bi bi-plus-lg"></i>
                <span>Nowa umowa zadatku</span>
            </button>
        </div>

        <!-- LISTA UMÓW ZADATKU -->
        <div class="card border shadow-sm">
            <div class="card-body">
                <div class="table-responsive">
                    <Vue3EasyDataTable :headers="headers" :items="contracts" :loading="loading" buttons-pagination show-index>
                        <template #item-companyName="item">
                            <span>
                                {{ item.companyName ? item.companyName : `${item.firstName || ''} ${item.lastName || ''}`.trim() || '—' }}
                            </span>
                        </template>
                        <template #item-actions="item">
                            <div class="d-flex justify-content-end gap-1 py-1">
                                <button class="btn btn-sm btn-outline-primary" title="Edytuj umowę" @click="editContract(item)">
                                    <i class="bi bi-pencil"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-secondary" title="Pobierz Umowę PDF" @click="downloadPDF(item)">
                                    <i class="bi bi-file-earmark-pdf"></i>
                                </button>
                                <button class="btn btn-sm btn-outline-danger" title="Usuń" @click="deleteContract(item.ID || item.id)">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- MODAL TWORZENIA / EDYCJI -->
        <div ref="contractModalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">Nowa umowa zadatku</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <div class="modal-body p-4">
                        <form @submit.prevent>
                            <DepositSection1 v-model="form" title="Dane dokumentu" :lastDocuments="lastContracts" />

                            <InvoiceSection2 v-model="form.vehicle" :vehicleAddons="[]" 
                                    @openVehicleSelector="openVehicleSelector" 
                                    title="Dane pojazdu"
                            />
                            <VehicleSelectorModal ref="vehicleSelectorModalRef" @select="onVehicleSelected" />

                            <InvoiceSection4 v-model="form.seller" :dicts="dicts" title="Sprzedawca" />
                            <InvoiceSection5 v-model="form.buyer" :dicts="dicts" @fetchGus="fetchGusData" title="Nabywca" />
                            <InvoiceSection6 v-model="form.coOwner" :dicts="dicts" title="Współwłaściciel" />

                            <!-- Sekcja ceny, zadatku oraz rozpisanych płatności -->
                            <DepositSection7 v-model="form" v-model:sellerModel="form.seller" :dicts="dicts" title="Cena i zadatek" />

                            <DepositSectionProvisions v-model="form.provisions" />
                            <DepositSectionNotes v-model="form.notes" />

                            <div v-if="missingWarnings.length > 0" class="alert alert-warning mb-3">
                                <div class="fw-bold mb-1">W dokumencie brakuje istotnych danych (nie są obowiązkowe) - zweryfikuj brakujące pola:</div>
                                <ul class="mb-0 ps-3">
                                    <li v-for="warn in missingWarnings" :key="warn" class="fst-italic">{{ warn }}</li>
                                </ul>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer bg-light d-flex justify-content-between flex-wrap gap-2">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Anuluj</button>
                        <div class="d-flex gap-2">
                            <button type="button" class="btn btn-success" :disabled="saving" @click="saveContract">
                                <i class="bi bi-save me-1"></i>Zapisz
                            </button>
                            <button type="button" class="btn btn-primary" :disabled="saving" @click="saveAndGeneratePDF">
                                <i class="bi bi-file-earmark-pdf me-1"></i>Zapisz i wygeneruj PDF
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <AppLoader :show="saving || loading" :text="loaderText" />
        <div style="position: absolute; left: -9999px; top: -9999px;">
            <DepositContractTemplate ref="contractPdfRenderArea" v-if="pdfContractDetails" :contract="pdfContractDetails" />
        </div>
    </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick  } from 'vue'
import apiClient from '@/api'
import html2pdf from 'html2pdf.js'

import Modal from 'bootstrap/js/dist/modal'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import AppLoader from '@/components/AppLoader.vue'
import { webAlert, showConfirm } from '@/utils/dialog'

import InvoiceSection2 from '@/components/invoices/InvoiceSection2.vue'
import InvoiceSection4 from '@/components/invoices/InvoiceSection4.vue'
import InvoiceSection5 from '@/components/invoices/InvoiceSection5.vue'
import InvoiceSection6 from '@/components/invoices/InvoiceSection6.vue'
import VehicleSelectorModal from '@/components/invoices/VehicleSelectorModal.vue'
import DepositSection7 from '@/components/deposit/DepositSection7.vue'
import DepositSection1 from '@/components/deposit/DepositSection1.vue'

import DepositSectionProvisions from '@/components/deposit/DepositSectionProvisions.vue'
import DepositSectionNotes from '@/components/deposit/DepositSectionNotes.vue'
import DepositContractTemplate from '@/components/templates/DepositContractTemplate.vue' // lub nazwa Twojego szablonu PDF dla zadatku
const pdfContractDetails = ref(null)
const contractPdfRenderArea = ref(null)

const loading = ref(false)
const saving = ref(false)
const loaderText = ref('')
const contracts = ref([])
const lastContracts = ref([])
const contractModalRef = ref(null)
let contractModalInstance = null
const vehicleSelectorModalRef = ref(null)

const headers = [
    { text: 'Numer umowy', value: 'docNumber', sortable: true },
    { text: 'Nabywca', value: 'companyName', sortable: true },
    { text: 'Kwota zadatku', value: 'depositAmount', sortable: true },
    { text: 'Data wystawienia', value: 'issueDate', sortable: true },
    { text: 'Akcje', value: 'actions', width: 180 }
]

const dicts = reactive({
    currencies: [],
    countries: [],
    paymentTypes: []
})

const fetchDict = async (action, extraData = {}) => {
    try {
        const res = await apiClient.post({ action, ...extraData })
        return Array.isArray(res.data?.data) ? res.data.data : []
    } catch { return [] }
}

const loadDictionaries = async () => {
    dicts.currencies = await fetchDict('get-currencies')
    dicts.countries = await fetchDict('get-countries')
    dicts.paymentTypes = await fetchDict('get-payment-types')
}

const fetchLastContracts = async () => {
    try {
        const response = await apiClient.post({ action: 'get-last-deposit-contracts' })
        const rawData = response.data?.data || response.data || []
        
        lastContracts.value = rawData.map(item => {
            if (typeof item === 'object' && item !== null) {
                return {
                    docNumber: item.docNumber,
                    createDate: item.issueDate || item.createDate
                }
            }
            return { docNumber: item, createDate: '' }
        })
    } catch (error) {
        console.error('Błąd pobierania ostatnich umów zadatku:', error)
        lastContracts.value = []
    }
}

const createEmptyParty = () => ({
    nip: '', companyName: '', firstName: '', lastName: '', country: 'Polska',
    postalCode: '', city: '', street: '', email: '', phone: '', pesel: '',
    idCardSeriesNumber: '', notes: '', bankName: '', bankAccount: ''
})

const defaultProvisions = `2. Kupujący wpłaca zadatek na poczet zakupu pojazdu w wysokości: {kwotazadatku}
3. Umowa sprzedaży zabezpieczona niniejszym zadatkiem zostanie zawarta do dnia {datakoncaumowy}
4. W przypadku niedotrzymania terminu lub wycofania się kupującego z umowy zadatek przechodzi na rzecz sprzedającego (komisanta) na podstawie Art. 394 KPC
5. W sprawach nie uregulowanych niniejszą umową mają zastosowanie przepisy Kodeksu Cywilnego`

const createEmptyForm = () => ({
    ID: null,
    docNumber: '',
    correctedInvoiceNumber: '',
    issueDate: new Date().toISOString().substring(0, 10),
    issuePlace: '',
    vehicle: {
        id: null, 
        make: '', 
        model: '', 
        engineCapacity: '', 
        mileage: '', 
        gearbox: '',
        fuelType: '', 
        powerHp: '', 
        color: '', 
        vin: '', 
        type: '',
        registrationPlate: '',
        productionYear: '', 
        firstRegistrationDate: '', 
        additionalInfo: ''
    },
    ocState: '',
    keysState: '',
    regBookState: '',
    regStickerState: '',
    platesState: '',
    carCardState: '',
    exciseTaxState: '',
    serviceBookState: '',
    additionalEquipment: '',
    vehicleStateNotes: '',
    buyer: createEmptyParty(),
    coOwner: createEmptyParty(),
    seller: createEmptyParty(),
    carPrice: 0,
    carCurrency: 'PLN',
    depositAmount: 0,
    depositCurrency: 'PLN',
    agreementEndDate: '',
    euroRate: '',
    euroRateDate: '',
    // Lista podzielonych płatności
    payments: [
        {
            amount: 0,
            paymentType: 'Gotówka',
            dueDate: '',
            currency: 'PLN'
        }
    ],
    provisions: defaultProvisions,
    notes: ''
})

const form = reactive(createEmptyForm())

const missingWarnings = computed(() => {
    const warnings = []
    if (!form.vehicle.make) warnings.push('brak podanej marki pojazdu')
    if (!form.carPrice || Number(form.carPrice) <= 0) warnings.push('brak ceny pojazdu')
    if (!form.depositAmount || Number(form.depositAmount) <= 0) warnings.push('brak kwoty zadatku')
    if (!form.issueDate) warnings.push('brak daty wystawienia')
    return warnings
})

const loadContracts = async () => {
    loading.value = true
    loaderText.value = 'Pobieranie umów zadatku...'
    try {
        const response = await apiClient.post({ action: 'get-deposit-contracts' })
        contracts.value = response.data?.data || []
    } catch (e) {
        webAlert('Błąd podczas pobierania umów zadatku.')
    } finally {
        loading.value = false
    }
}

const openVehicleSelector = () => vehicleSelectorModalRef.value?.show()

const onVehicleSelected = (vehicleData) => {
    Object.assign(form.vehicle, {
        id: vehicleData.ID || vehicleData.id,
        make: vehicleData.make || '',
        model: vehicleData.model || '',
        engineCapacity: vehicleData.engineCCM || vehicleData.engineCapacity || '',
        mileage: vehicleData.mileage || '',
        type: vehicleData.type || '',
        gearbox: vehicleData.gearboxTXT || vehicleData.gearbox || '',
        fuelType: vehicleData.fuelTXT || vehicleData.fuelType || '',
        powerHp: vehicleData.powerKM || vehicleData.powerHp || '',
        color: vehicleData.colourTXT || vehicleData.color || '',
        vin: vehicleData.VIN || vehicleData.vin || '',
        registrationPlate: vehicleData.registerNumber || vehicleData.registrationPlate || '',
        productionYear: vehicleData.productionYear || '',
        firstRegistrationDate: vehicleData.firstRegistrationDate || '',
        additionalInfo: vehicleData.additionalInfo || vehicleData.offerDesc || ''
    })
    if (vehicleData.price) form.carPrice = Number(vehicleData.price)
}

const fetchGusData = async (type) => {
    const target = form[type]
    if (!target.nip) return
    saving.value = true
    loaderText.value = 'Pobieranie z GUS...'
    try {
        const res = await apiClient.post({ action: 'get_data_from_gus', nip: target.nip })
        if (res.data?.success) {
            Object.assign(target, res.data.company)
            if (type === 'seller') form.issuePlace = target.city
        }
    } finally {
        saving.value = false
    }
}

const openModalForCreate = () => {
    Object.assign(form, createEmptyForm())
    fetchLastContracts()
    contractModalInstance.show()
}

const editContract = async (item) => {
    saving.value = true
    loaderText.value = 'Pobieranie danych umowy zadatku...'
    try {
        const response = await apiClient.post({ action: 'get-deposit-contract', ID: item.ID || item.id })
        const data = response.data?.data?.[0] || response.data?.data || response.data
        if (data) {
            Object.assign(form, createEmptyForm(), data)
            
            // Mapowanie tablicy płatności z API
            if (Array.isArray(data.payment) && data.payment.length > 0) {
                form.payments = data.payment.map(pay => ({
                    amount: pay.amount || data.carPrice || 0,
                    paymentType: pay.paymentType || 'Gotówka',
                    dueDate: pay.dueDate && pay.dueDate !== '0000-00-00' ? pay.dueDate : '',
                    currency: pay.currency || data.carCurrency || 'PLN',
                    bankName: pay.bankName || '',
                    bankAccount: pay.bankAccount || ''
                }))
            }
            onVehicleSelected(data?.car)
        }
        contractModalInstance.show()
    } catch (error) {
        webAlert('Nie udało się pobrać danych umowy zadatku.')
    } finally {
        saving.value = false
    }
}

const saveContract = async () => {
    if (Array.isArray(form.payments) && form.payments.length > 0) {
        for (let i = 0; i < form.payments.length; i++) {
            const pay = form.payments[i]
            if (!pay.amount || Number(pay.amount) <= 0) {
                webAlert(`Wiersz płatności nr ${i + 1}: Musisz podać prawidłową kwotę.`)
                return // Blokuje zapis
            }
            if (!pay.dueDate || pay.dueDate.trim() === '') {
                webAlert(`Wiersz płatności nr ${i + 1}: Musisz podać datę zapłaty.`)
                return // Blokuje zapis
            }
        }
    } else {
        webAlert('Musisz dodać przynajmniej jedną pozycję płatności.')
        return
    }
    
    saving.value = true
    loaderText.value = 'Zapisywanie umowy zadatku...'
    try {
        const response = await apiClient.post({ action: 'save-deposit-contract', contract: form })
        const resData = response.data || response

        if (resData?.status === 'success' || resData?.success || resData?.contractID || resData?.ID || resData?.id) {
            contractModalInstance.hide()
            await loadContracts()
            webAlert({ message: resData?.message || 'Umowa zadatku została zapisana.', type: 'success' })
        } else {
            webAlert(typeof resData === 'string' ? resData : 'Błąd podczas zapisywania umowy zadatku.')
        }
    } catch (e) {
        webAlert('Wystąpił błąd podczas połączenia z serwerem.')
    } finally {
        saving.value = false
    }
}

const saveAndGeneratePDF = async () => { 
    await saveContract() 
    // Po zapisie możemy odnaleźć i pobrać PDF dla ostatnio zapisanej lub otwartej umowy
    if (contracts.value.length > 0) {
        downloadPDF(contracts.value[0])
    }
}

const downloadPDF = async (item) => {
    saving.value = true
    loaderText.value = 'Pobieranie danych umowy i generowanie PDF...'
    try {
        const response = await apiClient.post({ action: 'get-deposit-contract', ID: item.ID || item.id })
        const data = response.data?.data?.[0] || response.data?.data || response.data

        if (!data) {
            webAlert('Nie udało się odnaleźć danych wybranej umowy.')
            return
        }

        pdfContractDetails.value = data
        await nextTick()

        const opt = {
            margin: 5,
            filename: `Umowa_Zadatku_${data.docNumber || item.ID}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        }

        const element = contractPdfRenderArea.value?.$el || contractPdfRenderArea.value?.element
        
        if (!element) {
            webAlert('Nie odnaleziono elementu DOM do wygenerowania PDF.')
            return
        }

        const pdfWorker = html2pdf().set(opt).from(element)
        const pdfBlob = await pdfWorker.output('blob')

        const blobUrl = URL.createObjectURL(pdfBlob)
        window.open(blobUrl, '_blank')

    } catch (error) {
        console.error('Błąd generowania PDF:', error)
        webAlert('Błąd podczas generowania pliku PDF umowy.')
    } finally {
        saving.value = false
    }
}

const deleteContract = async (id) => {
    const confirmed = await showConfirm('Czy na pewno chcesz usunąć tę umowę zadatku?', {
        title: 'Usuwanie pozycji',
        confirmText: 'Usuń',
        cancelText: 'Anuluj',
        confirmClass: 'btn-danger',
        icon: 'bi-trash text-danger'
    })
    if (!confirmed) return

    saving.value = true
    loaderText.value = "Kasowanie..."
    try {
        const response = await apiClient.post({ action: 'delete-deposit-contract', ID: id })
        await loadContracts()
        webAlert(response.data?.message || 'Usunięto pomyślnie.')
    } catch (error) {
        webAlert('Błąd podczas usuwania pozycji.')
    } finally {
        saving.value = false
    }
}

onMounted(async () => {
    await loadDictionaries()
    contractModalInstance = new Modal(contractModalRef.value)
    await loadContracts()
})
</script>

<style scoped>
.cursor-pointer { cursor: pointer; user-select: none; }
</style>